// src/screens/mypage/PointHistoryScreen.js
// 포인트 내역 화면 (28차 신규)
// - 전체 적립/사용/만료 이력
// - 30일 내 소멸 예정 포인트 배너
// 51차: 색 theme.js 단일화 — COLORS 참조 / 초록·빨강·주황·보라 통일
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체
// 77차: 74차 교체 시 제목 누락("‹ 뒤로"만 보이던 것 — "AppHeader 없음" 반복 신고 원인) 복원

import React, { useState, useCallback, useRef } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, RefreshControl,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import { EmptyPointsIcon, HourglassIcon, RewardIcon, IconLabel, WarningIcon } from '../../components/common/Icons';
import { getPointHistoryAll, getExpiringPoints } from '../../services/pointService';
import { COLORS } from '../../constants/theme';
import { thisMonthStr } from '../../utils/dateUtils';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, CARD = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text;
const TEXT_MUTED = COLORS.textMuted, TEXT_SEC = COLORS.textSec;
const GREEN = COLORS.green, RED = COLORS.red, ORANGE = COLORS.orange, PURPLE = COLORS.purple;

const FILTERS = [
  { key: 'all',    label: '전체' },
  { key: 'earn',   label: '적립' },
  { key: 'use',    label: '사용' },
  { key: 'expire', label: '만료' },
];

const typeConfig = (type, expired) => {
  if (expired || type === 'expire') return { color: TEXT_MUTED, sign: '', label: '만료',  emoji: '⏳', Icon: HourglassIcon };
  if (type === 'earn')              return { color: GREEN,      sign: '+', label: '적립',  emoji: '💎', Icon: RewardIcon };
  if (type === 'use')               return { color: RED,        sign: '-', label: '사용',  emoji: '🔻' };
  return                                   { color: TEXT_SEC,   sign: '',  label: type,   emoji: '•' };
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
  } catch { return dateStr; }
};

const fmtExpire = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d    = new Date(dateStr);
    const diff = Math.ceil((d - new Date()) / 86400000);
    if (diff <= 0)  return null;
    if (diff <= 7)  return `D-${diff} 소멸`;
    return `${fmtDate(dateStr)} 소멸`;
  } catch { return null; }
};

export default function PointHistoryScreen() {
  const insets = useSafeAreaInsets();
  const { user, profile } = useAuth();

  const [history,       setHistory]       = useState([]);
  const [expiring,      setExpiring]      = useState({ total: 0, items: [] });
  const [filter,        setFilter]        = useState('all');
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [loadError,     setLoadError]     = useState(false);

  const currentPoints = profile?.points ?? 0;

  // 79차: 화면을 빠르게 나갔다 다시 들어오면 이전 포커스의 load()가 늦게 응답해 최신
  // 내역을 오래된 것으로 덮어쓸 수 있었음(MyReviewsScreen.js가 74차에 이미 막아둔 것과
  // 동일 위험, 이 화면엔 빠져있었음) — activeRef로 이전 포커스 세션의 setState만 무시.
  const activeRef = useRef(true);
  const load = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setLoadError(false);
      const [hist, exp] = await Promise.all([
        getPointHistoryAll(user.uid),
        getExpiringPoints(user.uid),
      ]);
      if (!activeRef.current) return;
      setHistory(hist);
      setExpiring(exp);
    } catch (e) {
      if (!activeRef.current) return;
      console.error('[PointHistoryScreen] load 실패:', e.message);
      setLoadError(true);
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }, [user?.uid]);

  // 78차 38차: 크루 생성 등으로 포인트를 쓰고 바로 이 화면을 다시 봐도 "사용처" 내역이
  // 안 보인다는 신고 — 마운트 1회성 useEffect라 탭 재방문(리마운트 아님)으로는 재조회가
  // 안 됐던 것이 원인(BookingScreen.js/MyReviewsScreen.js와 동일 패턴의 버그).
  useFocusEffect(useCallback(() => {
    activeRef.current = true;
    load();
    return () => { activeRef.current = false; };
  }, [load]));

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const filtered = history.filter(item => {
    if (filter === 'all')    return true;
    if (filter === 'expire') return item.expired === true || item.type === 'expire';
    if (filter === 'earn')   return item.type === 'earn' && !item.expired;
    if (filter === 'use')    return item.type === 'use';
    return true;
  });

  // 월별 그룹핑
  const grouped = filtered.reduce((acc, item) => {
    const month = (item.date || item.createdAt || '').slice(0, 7); // 'YYYY-MM'
    const key   = month || '날짜 없음';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const months = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // 이번 달 적립/사용 집계
  const thisMonth = thisMonthStr();
  const thisEarn  = history.filter(i=>i.type==='earn'&&!i.expired&&(i.date||'').startsWith(thisMonth))
                           .reduce((s,i)=>s+(i.amount||0), 0);
  const thisUse   = history.filter(i=>i.type==='use'&&(i.date||'').startsWith(thisMonth))
                           .reduce((s,i)=>s+(i.amount||0), 0);

  if (loading) return (
    <View style={[s.container, { justifyContent: 'center', alignItems: 'center' }]}>
      <ActivityIndicator size="large" color={GOLD} />
    </View>
  );

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={{ color: COLORS.text, fontSize: 15, fontWeight: '800' }}>포인트 내역</Text>} />

      <ScrollView
        contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={GOLD} />}>

        {/* 현재 포인트 요약 카드 */}
        <View style={s.summaryCard}>
          <Text style={s.summaryLabel}>보유 포인트</Text>
          <IconLabel Icon={RewardIcon} size={s.summaryPoints.fontSize || 24} color={s.summaryPoints.color} textStyle={s.summaryPoints}>{currentPoints.toLocaleString()} P</IconLabel>
          <View style={s.summaryRow}>
            <View style={s.summaryItem}>
              <Text style={[s.summaryItemVal, { color: GREEN }]}>+{thisEarn.toLocaleString()}P</Text>
              <Text style={s.summaryItemLbl}>이번달 적립</Text>
            </View>
            <View style={s.summaryDivider} />
            <View style={s.summaryItem}>
              <Text style={[s.summaryItemVal, { color: RED }]}>-{Math.abs(thisUse).toLocaleString()}P</Text>
              <Text style={s.summaryItemLbl}>이번달 사용</Text>
            </View>
          </View>
        </View>

        {/* 30일 내 소멸 예정 배너 */}
        {expiring.total > 0 && (
          <View style={s.expiringBanner}>
            <View style={s.expiringLeft}>
              <WarningIcon size={s.expiringEmoji.fontSize || 22} color={s.expiringEmoji.color} />
              <View>
                <Text style={s.expiringTitle}>포인트 소멸 예정</Text>
                <Text style={s.expiringSub}>
                  30일 내 {expiring.total.toLocaleString()}P가 소멸될 예정이에요
                </Text>
              </View>
            </View>
            <View style={s.expiringBadge}>
              <Text style={s.expiringBadgeTxt}>{expiring.total.toLocaleString()}P</Text>
            </View>
          </View>
        )}

        {/* 필터 탭 */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={{ paddingHorizontal: 16, marginBottom: 8 }}
          contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
          {FILTERS.map(f => (
            <TouchableOpacity key={f.key}
              style={[s.filterChip, filter === f.key && s.filterChipOn]}
              onPress={() => setFilter(f.key)}>
              <Text style={[s.filterChipTxt, filter === f.key && s.filterChipTxtOn]}>{f.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* 내역 없음 / 로드 실패 */}
        {filtered.length === 0 && (
          <View style={s.empty}>
            <EmptyPointsIcon size={64} />
            {loadError ? (
              <>
                <Text style={{ color: TEXT_MUTED, fontSize: 13, marginTop: 12 }}>포인트 내역을 불러오지 못했어요</Text>
                <TouchableOpacity onPress={load} style={{ marginTop: 12 }}>
                  <Text style={{ color: GOLD, fontSize: 13, fontWeight: '700' }}>다시 시도</Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={{ color: TEXT_MUTED, fontSize: 13, marginTop: 12 }}>포인트 내역이 없어요</Text>
            )}
          </View>
        )}

        {/* 월별 그룹 */}
        {months.map(month => (
          <View key={month} style={{ marginBottom: 8 }}>
            <Text style={s.monthLabel}>{month.replace('-', '년 ')}월</Text>
            <View style={s.monthCard}>
              {grouped[month].map((item, idx) => {
                const cfg      = typeConfig(item.type, item.expired);
                const expireAt = fmtExpire(item.expiresAt);
                const isLast   = idx === grouped[month].length - 1;
                return (
                  <View key={item.id}
                    style={[s.historyRow, !isLast && s.historyRowBorder]}>
                    {/* 아이콘 */}
                    <View style={[s.historyIcon, { backgroundColor: cfg.color + '18' }]}>
                      {cfg.Icon ? <cfg.Icon size={16} color={cfg.color} /> : <Text style={{ fontSize: 16 }}>{cfg.emoji}</Text>}
                    </View>
                    {/* 내용 */}
                    <View style={{ flex: 1 }}>
                      <Text style={s.historyReason} numberOfLines={1}>
                        {item.reason || '-'}
                      </Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <Text style={s.historyDate}>{fmtDate(item.date || item.createdAt)}</Text>
                        {expireAt && (
                          <View style={s.expireAtBadge}>
                            <Text style={s.expireAtTxt}>{expireAt}</Text>
                          </View>
                        )}
                      </View>
                    </View>
                    {/* 금액 */}
                    <Text style={[s.historyAmount, { color: cfg.color }]}>
                      {/* 78차 66차: type:'use' 내역은 서버가 amount 자체를 음수로 저장하는데
                          (functions/index.js), 여기서 cfg.sign('-')을 또 붙여 "--50P"처럼
                          마이너스가 중복 표기되던 버그 — 절댓값으로 표시. */}
                      {item.expired ? '' : cfg.sign}{Math.abs(item.amount || 0).toLocaleString()}P
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container:    { flex: 1, backgroundColor: BG },

  // 요약 카드
  summaryCard:  { margin: 16, padding: 20, backgroundColor: CARD,
                  borderRadius: 16, borderWidth: 1, borderColor: BORDER,
                  borderTopWidth: 2, borderTopColor: GOLD,
                  shadowColor: '#000', shadowOffset: { width: 0, height: 3 },
                  shadowOpacity: 0.25, shadowRadius: 8, elevation: 4 },
  summaryLabel: { color: TEXT_MUTED, fontSize: 11, fontWeight: '700', letterSpacing: 0.8, marginBottom: 6 },
  summaryPoints:{ color: GOLD, fontSize: 28, fontWeight: '900', marginBottom: 16 },
  summaryRow:   { flexDirection: 'row', alignItems: 'center' },
  summaryItem:  { flex: 1, alignItems: 'center' },
  summaryItemVal:{ fontSize: 16, fontWeight: '800' },
  summaryItemLbl:{ color: TEXT_MUTED, fontSize: 11, marginTop: 3 },
  summaryDivider:{ width: 1, height: 36, backgroundColor: BORDER, marginHorizontal: 8 },

  // 소멸 예정 배너
  expiringBanner: {
    marginHorizontal: 16, marginBottom: 12,
    backgroundColor: ORANGE + '1A',
    borderRadius: 12, borderWidth: 1, borderColor: ORANGE + '59',
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 14,
  },
  expiringLeft:    { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
  expiringEmoji:   { fontSize: 22 },
  expiringTitle:   { color: ORANGE, fontSize: 13, fontWeight: '800' },
  expiringSub:     { color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 },
  expiringBadge:   { backgroundColor: ORANGE + '20', borderRadius: 8, borderWidth: 1,
                     borderColor: ORANGE + '60', paddingHorizontal: 10, paddingVertical: 5 },
  expiringBadgeTxt:{ color: ORANGE, fontSize: 13, fontWeight: '800' },

  // 필터
  filterChip:    { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20,
                   borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE },
  filterChipOn:  { backgroundColor: GOLD + '18', borderColor: GOLD + '50' },
  filterChipTxt: { color: TEXT_MUTED, fontSize: 12 },
  filterChipTxtOn:{ color: GOLD, fontWeight: '700' },

  // 월별
  monthLabel:   { color: TEXT_MUTED, fontSize: 11, fontWeight: '700', letterSpacing: 0.8,
                  marginHorizontal: 16, marginBottom: 6, marginTop: 8 },
  monthCard:    { marginHorizontal: 16, backgroundColor: CARD,
                  borderRadius: 12, borderWidth: 1, borderColor: BORDER, overflow: 'hidden' },

  // 내역 행
  historyRow:       { flexDirection: 'row', alignItems: 'center', gap: 12,
                      paddingHorizontal: 14, paddingVertical: 14 },
  historyRowBorder: { borderBottomWidth: 1, borderBottomColor: BORDER },
  historyIcon:      { width: 38, height: 38, borderRadius: 10,
                      alignItems: 'center', justifyContent: 'center' },
  historyReason:    { color: TEXT, fontSize: 13, fontWeight: '600' },
  historyDate:      { color: TEXT_MUTED, fontSize: 11 },
  historyAmount:    { fontSize: 15, fontWeight: '800', minWidth: 70, textAlign: 'right' },

  // 만료 예정 배지 (행 내)
  expireAtBadge: { backgroundColor: ORANGE + '1F', borderRadius: 4, borderWidth: 1,
                   borderColor: ORANGE + '59', paddingHorizontal: 5, paddingVertical: 1 },
  expireAtTxt:   { color: ORANGE, fontSize: 10, fontWeight: '700' },

  empty:        { alignItems: 'center', paddingVertical: 60 },
});